import axios from 'axios';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { formatPhoneNumber } from '@/lib/phoneUtils';
import { ArrowLeft, Save, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function CreerClient() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    nom: '',
    type_client: 'residentiel',
    telephone: '',
    email: '',
    adresse_client: '',
    ville_client: '',
    code_postal_client: '',
    utilise_adresse_client_facturation: true,
    adresse_facturation: '',
    ville_facturation: '',
    code_postal_facturation: '',
    notes_generales: '',
    // Contact commercial (si applicable)
    contact_commercial_nom: '',
    contact_commercial_telephone: '',
    contact_commercial_email: '',
    contact_commercial_poste: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom || !formData.telephone || !formData.adresse_client) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);

    try {
      // Préparer le payload en nettoyant les champs
      const payload = {
        nom: formData.nom,
        type_client: formData.type_client,
        telephone: formData.telephone,
        email: formData.email || null,
        adresse_client: formData.adresse_client,
        ville_client: formData.ville_client,
        code_postal_client: formData.code_postal_client,
        utilise_adresse_client_facturation: formData.utilise_adresse_client_facturation,
        adresse_facturation: formData.utilise_adresse_client_facturation ? null : formData.adresse_facturation,
        ville_facturation: formData.utilise_adresse_client_facturation ? null : formData.ville_facturation,
        code_postal_facturation: formData.utilise_adresse_client_facturation ? null : formData.code_postal_facturation,
        notes_generales: formData.notes_generales || null,
        actif: true,
      };

      // Ajouter contact commercial si applicable
      if (formData.type_client === 'commercial' && formData.contact_commercial_nom) {
        payload.contact_commercial = {
          nom: formData.contact_commercial_nom,
          telephone: formData.contact_commercial_telephone,
          email: formData.contact_commercial_email || null,
          poste: formData.contact_commercial_poste || null,
        };
      }

      const token = localStorage.getItem('auth_token');
      const response = await axios.post(`${API_URL}/api/crm/clients`, payload, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status !== 200) throw new Error('Erreur lors de la création');

      const data = response.data;
      toast.success('Client créé avec succès!');
      
      // Demander si l'utilisateur veut créer un dossier maintenant
      const creerDossier = window.confirm('Client créé! Voulez-vous créer un dossier pour ce client maintenant?');
      if (creerDossier) {
        navigate('/crm/creer-dossier');
      } else {
        navigate('/crm/dossiers');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la création du client');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Créer un Nouveau Client</h1>
        <p className="text-gray-600 mt-1">Ajoutez un nouveau client dans votre système CRM</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Informations Générales */}
          <Card>
            <CardHeader>
              <CardTitle>Informations Générales</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="nom">
                  Nom du Client <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nom"
                  placeholder="Nom complet ou nom de l'entreprise"
                  value={formData.nom}
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="type_client">Type de Client</Label>
                <Select
                  value={formData.type_client}
                  onValueChange={(val) => setFormData({ ...formData, type_client: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residentiel">Résidentiel</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="telephone">
                    Téléphone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="telephone"
                    placeholder="(514) 555-1234"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: formatPhoneNumber(e.target.value) })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="email@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Commercial (si applicable) */}
          {formData.type_client === 'commercial' && (
            <Card>
              <CardHeader>
                <CardTitle>Contact Commercial (Optionnel)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_nom">Nom</Label>
                    <Input
                      id="contact_nom"
                      placeholder="Jean Tremblay"
                      value={formData.contact_commercial_nom}
                      onChange={(e) => setFormData({ ...formData, contact_commercial_nom: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_poste">Poste</Label>
                    <Input
                      id="contact_poste"
                      placeholder="Gérant"
                      value={formData.contact_commercial_poste}
                      onChange={(e) =>
                        setFormData({ ...formData, contact_commercial_poste: e.target.value })
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="contact_telephone">Téléphone</Label>
                    <Input
                      id="contact_telephone"
                      placeholder="(514) 555-5678"
                      value={formData.contact_commercial_telephone}
                      onChange={(e) =>
                        setFormData({ ...formData, contact_commercial_telephone: formatPhoneNumber(e.target.value) })
                      }
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_email">Email</Label>
                    <Input
                      id="contact_email"
                      type="email"
                      placeholder="contact@example.com"
                      value={formData.contact_commercial_email}
                      onChange={(e) =>
                        setFormData({ ...formData, contact_commercial_email: e.target.value })
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Adresse Client */}
          <Card>
            <CardHeader>
              <CardTitle>Adresse du Client</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="adresse_client">
                  Adresse <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="adresse_client"
                  placeholder="123 Rue Principale"
                  value={formData.adresse_client}
                  onChange={(e) => setFormData({ ...formData, adresse_client: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ville_client">
                    Ville <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="ville_client"
                    placeholder="Montréal"
                    value={formData.ville_client}
                    onChange={(e) => setFormData({ ...formData, ville_client: e.target.value })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="code_postal_client">
                    Code Postal <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="code_postal_client"
                    placeholder="H1A 1A1"
                    value={formData.code_postal_client}
                    onChange={(e) => setFormData({ ...formData, code_postal_client: e.target.value })}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Adresse Facturation */}
          <Card>
            <CardHeader>
              <CardTitle>Adresse de Facturation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="utilise_adresse_client"
                  checked={formData.utilise_adresse_client_facturation}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, utilise_adresse_client_facturation: checked })
                  }
                />
                <Label htmlFor="utilise_adresse_client" className="cursor-pointer">
                  Utiliser la même adresse que le client
                </Label>
              </div>

              {!formData.utilise_adresse_client_facturation && (
                <>
                  <div>
                    <Label htmlFor="adresse_facturation">Adresse</Label>
                    <Input
                      id="adresse_facturation"
                      placeholder="123 Rue Principale"
                      value={formData.adresse_facturation}
                      onChange={(e) => setFormData({ ...formData, adresse_facturation: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="ville_facturation">Ville</Label>
                      <Input
                        id="ville_facturation"
                        placeholder="Montréal"
                        value={formData.ville_facturation}
                        onChange={(e) => setFormData({ ...formData, ville_facturation: e.target.value })}
                      />
                    </div>

                    <div>
                      <Label htmlFor="code_postal_facturation">Code Postal</Label>
                      <Input
                        id="code_postal_facturation"
                        placeholder="H1A 1A1"
                        value={formData.code_postal_facturation}
                        onChange={(e) =>
                          setFormData({ ...formData, code_postal_facturation: e.target.value })
                        }
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Notes Générales</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                placeholder="Notes sur le client..."
                value={formData.notes_generales}
                onChange={(e) => setFormData({ ...formData, notes_generales: e.target.value })}
                rows={4}
              />
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Création...' : 'Créer le Client'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
